/**
 * Alpha 汎用エラーモーダル。
 * - 呼び出し: `AlphaErrorModal.show({ title, content })`
 *             `AlphaErrorModal.hide()`
 * - DOM は `snippets/alpha-error-modal.liquid` が body 末尾に 1 回だけ描画する前提。
 */
(function () {
  const ROOT_ID = 'alpha-error-modal';
  const CLOSE_ATTR = 'data-alpha-error-modal-close';
  const CLASS_CLOSING = 'alpha-error-modal--closing';
  const CLASS_SCROLL_LOCK = 'alpha-error-modal-scroll-locked';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const state = {
    rootElement: null,
    titleElement: null,
    contentElement: null,
    containerElement: null,
    closeButtonElement: null,
    lastFocusedElement: null,
    scrollY: 0,
    isOpen: false,
    pendingCloseTimer: null,
  };

  /**
   * モーダル DOM を参照キャッシュする。snippet が body 末尾に render されている前提。
   * 無ければ console.warn のみでサイレント（他機能に影響させない）。
   * @returns {boolean}
   */
  function captureRefs() {
    const root = document.getElementById(ROOT_ID);
    if (!root) {
      console.warn(
        '[AlphaErrorModal] snippet が未描画です (#' +
          ROOT_ID +
          ')。layout/theme.liquid に render 追加を確認してください。',
      );
      return false;
    }
    state.rootElement = root;
    state.titleElement = root.querySelector('#alpha-error-modal-title');
    state.contentElement = root.querySelector('#alpha-error-modal-content');
    state.containerElement = root.querySelector('.alpha-error-modal__container');
    state.closeButtonElement = root.querySelector('.alpha-error-modal__close');
    return true;
  }

  /**
   * 閉じるトリガー（オーバーレイ / ×ボタン）は `data-alpha-error-modal-close` 属性の有無で判定する。
   * SVG アイコン経由のクリックも親要素で拾えるよう closest で辿る。
   * @param {Event} event
   */
  function onRootClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    const closeTrigger = target.closest('[' + CLOSE_ATTR + ']');
    if (closeTrigger && state.rootElement.contains(closeTrigger)) {
      event.preventDefault();
      hide();
    }
  }

  /**
   * ESC で閉じる / Tab / Shift+Tab のフォーカストラップ。
   * @param {KeyboardEvent} event
   */
  function onKeydown(event) {
    if (!state.isOpen) {
      return;
    }
    if (event.key === 'Escape' || event.keyCode === 27) {
      event.preventDefault();
      hide();
      return;
    }
    if (event.key === 'Tab' || event.keyCode === 9) {
      trapFocus(event);
    }
  }

  /**
   * モーダル内の focusable 要素間で Tab 循環させる。
   * 候補が 1 つ以下なら閉じるボタンへ固定フォーカス。
   * @param {KeyboardEvent} event
   */
  function trapFocus(event) {
    const focusableCandidates = state.containerElement
      ? state.containerElement.querySelectorAll(FOCUSABLE_SELECTOR)
      : [];
    if (focusableCandidates.length === 0) {
      event.preventDefault();
      if (state.closeButtonElement) {
        state.closeButtonElement.focus();
      }
      return;
    }
    const firstElement = focusableCandidates[0];
    const lastElement = focusableCandidates[focusableCandidates.length - 1];
    const activeElement = document.activeElement;
    if (event.shiftKey) {
      if (activeElement === firstElement || !state.containerElement.contains(activeElement)) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      if (activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  /**
   * html / body にロッククラスを当て、body を position:fixed で固定してスクロール位置を保持する。
   * iOS Safari の慣性スクロール対策として body の top に現在の scrollY をマイナス指定する。
   */
  function lockScroll() {
    state.scrollY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.classList.add(CLASS_SCROLL_LOCK);
    document.body.classList.add(CLASS_SCROLL_LOCK);
    document.body.style.top = '-' + state.scrollY + 'px';
  }

  /**
   * スクロール位置を復元し、ロッククラスを外す。
   * 事前に top を消さないと復元直後にガタつく。
   */
  function unlockScroll() {
    document.documentElement.classList.remove(CLASS_SCROLL_LOCK);
    document.body.classList.remove(CLASS_SCROLL_LOCK);
    document.body.style.top = '';
    window.scrollTo(0, state.scrollY);
  }

  /**
   * 公開 API: モーダルを開いてタイトル/コンテンツを差し替える。
   * - 既に開いている場合は中身の差し替えのみ（閉じない / アニメも再生しない）。
   * - title は必須。content は任意。テキスト挿入は textContent のみ使用。
   * @param {{ title: string, content?: string }} options
   */
  function show(options) {
    if (!state.rootElement && !captureRefs()) {
      return;
    }
    const titleText = options && options.title != null ? String(options.title) : '';
    const contentText = options && options.content != null ? String(options.content) : '';

    if (state.titleElement) {
      state.titleElement.textContent = titleText;
    }
    if (state.contentElement) {
      state.contentElement.textContent = contentText;
      if (contentText === '') {
        state.contentElement.setAttribute('hidden', '');
      } else {
        state.contentElement.removeAttribute('hidden');
      }
    }

    if (state.isOpen) {
      return;
    }

    if (state.pendingCloseTimer) {
      clearTimeout(state.pendingCloseTimer);
      state.pendingCloseTimer = null;
    }
    state.rootElement.classList.remove(CLASS_CLOSING);
    state.lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    lockScroll();
    state.rootElement.setAttribute('aria-hidden', 'false');
    state.isOpen = true;

    if (state.closeButtonElement) {
      state.closeButtonElement.focus({ preventScroll: true });
    }
  }

  /**
   * 公開 API: モーダルを閉じる。
   * closing クラスでアニメを保持し、animationend を待ってから aria-hidden を戻す。
   * アニメがそもそも流れない環境（prefers-reduced-motion 等）向けに 400ms のタイムアウトも併設。
   */
  function hide() {
    if (!state.rootElement || !state.isOpen) {
      return;
    }

    state.rootElement.classList.add(CLASS_CLOSING);

    const finalize = function () {
      state.rootElement.classList.remove(CLASS_CLOSING);
      state.rootElement.setAttribute('aria-hidden', 'true');
      if (state.titleElement) {
        state.titleElement.textContent = '';
      }
      if (state.contentElement) {
        state.contentElement.textContent = '';
        state.contentElement.setAttribute('hidden', '');
      }
      unlockScroll();
      state.isOpen = false;
      if (state.lastFocusedElement && typeof state.lastFocusedElement.focus === 'function') {
        try {
          state.lastFocusedElement.focus({ preventScroll: true });
        } catch (e) {
          /* noop */
        }
      }
      state.lastFocusedElement = null;
      if (state.pendingCloseTimer) {
        clearTimeout(state.pendingCloseTimer);
        state.pendingCloseTimer = null;
      }
    };

    const onAnimationEnd = function (event) {
      // container のアニメ終了だけを契機にする（overlay と container で2回発火するため）
      if (event.target !== state.containerElement) {
        return;
      }
      state.containerElement.removeEventListener('animationend', onAnimationEnd);
      finalize();
    };

    if (state.containerElement) {
      state.containerElement.addEventListener('animationend', onAnimationEnd);
    }
    // アニメが走らない場合のフォールバック（prefers-reduced-motion / アニメ未適用 DOM）
    state.pendingCloseTimer = setTimeout(function () {
      if (state.containerElement) {
        state.containerElement.removeEventListener('animationend', onAnimationEnd);
      }
      finalize();
    }, 100);
  }

  /**
   * DOMContentLoaded 時にルート DOM を参照し、閉じる系イベントを仕掛ける。
   * ルートが無い場合は何もしない。
   */
  function init() {
    if (!captureRefs()) {
      return;
    }
    state.rootElement.addEventListener('click', onRootClick);
    document.addEventListener('keydown', onKeydown);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.AlphaErrorModal = {
    show: show,
    hide: hide,
  };
})();
